import { createClient } from '@supabase/supabase-js';
import { env } from '@/env.generated';
import { passwordRecoveryTarget } from '@/lib/auth-recovery';
import { syncQueryCacheOwner } from '@/lib/query-client';

/**
 * Browser Supabase client. Persists the session in localStorage (SPA model —
 * no SSR cookies). The access token is attached to API requests by lib/api.ts
 * and lib/utils/api.ts via {@link getAccessToken}.
 */
export const supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
	auth: {
		persistSession: true,
		autoRefreshToken: true,
		detectSessionInUrl: true,
	},
});

/**
 * In-memory copy of the current access token, kept in sync with the auth state.
 *
 * ADR-006: every API call used to `await supabase.auth.getSession()` before
 * `fetch`, which adds an await + `navigator.locks` contention to *every*
 * request. `supabase-js` already keeps the session in memory and refreshes it
 * on a timer (`autoRefreshToken`), firing `onAuthStateChange` on every change —
 * so we mirror the token here and read it synchronously.
 */
let currentAccessToken: string | null = null;

/**
 * Resolves once the persisted session has been read at startup. The API
 * wrappers await this before the first request so a cold load doesn't fire an
 * unauthenticated call while `getSession()` is still in flight.
 */
export const authReady: Promise<void> = supabase.auth.getSession().then(({ data }) => {
	currentAccessToken = data.session?.access_token ?? null;
});

// INITIAL_SESSION, SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY.
supabase.auth.onAuthStateChange((event, session) => {
	currentAccessToken = session?.access_token ?? null;
	// Un altro utente, o nessuno: i dati in cache non sono suoi.
	syncQueryCacheOwner(session?.user?.id ?? null);
	// Un link di recupero porta sempre al form della password, anche se Supabase
	// l'ha fatto atterrare altrove — la Site URL, cioè la dashboard (INB-362).
	// Registrato qui, accanto alla creazione del client, sente l'evento che il
	// client emette leggendo i token dall'URL.
	const target =
		typeof window === 'undefined' ? null : passwordRecoveryTarget(event, window.location.pathname);
	if (target) window.location.replace(target);
});

/** Current access token, or null when signed out. Synchronous. */
export function getAccessToken(): string | null {
	return currentAccessToken;
}

/**
 * Force a refresh of the cached token from `supabase-js` and return it. Use only
 * on the 401 retry path — the cached value is otherwise kept fresh by
 * `onAuthStateChange`.
 */
export async function refreshAccessToken(): Promise<string | null> {
	const { data } = await supabase.auth.getSession();
	currentAccessToken = data.session?.access_token ?? null;
	return currentAccessToken;
}
